// types
import {
  DayInWeekRequest,
  EventResponse,
  PlanningEntityIdentifier,
  PlanningEntityResponse
} from 'generated/index';
import { AppointmentFormFieldEntity } from './AppointmentForm.types';

// utils
import { flatMap, groupBy } from 'lodash';
import dayjs from 'dayjs';
import i18n from 'config/i18n';
import { getLocale } from 'utils/date';
import { createCalendarAppointmentEventFromPlanningEventBuilder } from '../Calendar/Calendar.utils';
import { DayIndex } from './components/RecurrencePatternBuilder/RecurrencePatternBuilder.types';

const t = i18n.getFixedT(getLocale(), 'common', 'mrp.entityTypeMap');

const mergeOverlappingOrAdjacentEvents = (
  events: EventResponse[]
): EventResponse[] => {
  if (events.length === 0) return [];

  const sorted = [...events].sort((a, b) =>
    dayjs(a.start).diff(dayjs(b.start))
  );
  const merged: EventResponse[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];

    const lastEnd = dayjs(last.end);
    const currentStart = dayjs(current.start);

    if (currentStart.diff(lastEnd, 'minute') <= 0) {
      last.end = dayjs.max(lastEnd, dayjs(current.end)).toISOString();
    } else {
      merged.push(current);
    }
  }

  return merged;
};

export const groupAndMergePlanningEntityEventsByType = (
  planningEntities: PlanningEntityResponse[]
) => {
  const grouped = groupBy(planningEntities ?? [], (x) => x.type);

  const merged = flatMap(Object.entries(grouped), ([typeKey, group]) => {
    const allEvents: EventResponse[] = group
      .flatMap(({ schedules, events }) =>
        schedules.flatMap(({ events }) => events).concat(events)
      )
      .filter((x) => x.category != 'FREE');

    const mergedEvents = mergeOverlappingOrAdjacentEvents(allEvents).map(
      createCalendarAppointmentEventFromPlanningEventBuilder({
        titleBuilder: () => t(`${typeKey}.plural`)
      })
    );

    return mergedEvents;
  });

  return merged;
};

export const appointmentFormFieldEntitiesToPlanningEntityIdentifiers = (
  fieldEntities: AppointmentFormFieldEntity[]
) => {
  return fieldEntities
    .filter(Boolean)
    .filter(
      ({ entityId, id, externalEntityId, externalId }) =>
        entityId || id || externalEntityId || externalId
    )
    .map(
      ({ label: _ = '', ...pei }) => pei
    ) satisfies PlanningEntityIdentifier[];
};

export const convertWeekDay = (idx: DayIndex): DayInWeekRequest => {
  switch (idx) {
    case 0:
      return 'MONDAY';
    case 1:
      return 'TUESDAY';
    case 2:
      return 'WEDNESDAY';
    case 3:
      return 'THURSDAY';
    case 4:
      return 'FRIDAY';
    case 5:
      return 'SATURDAY';
    case 6:
      return 'SUNDAY';
  }
};

export const sortLabel = (a: { label?: string }, b: { label?: string }) =>
  (a.label ?? '').localeCompare(b.label ?? '');
